import { DMA_ONBOARDING_PATHS } from '@upright/common/iam/users/dma-onboarding-constants';
import { DMA_ONBOARDING_NEXT_URL_PARAM } from '@upright/common/iam/users/dma-onboarding-next-url';
import { clearLegacyAuthTokensFromStorage } from '@upright/ui/common/generic/utilities/storage';
import { appendSearchParamsToUrlPath } from '../../../../common/utils/url-management';
import { LoginViewLocationState } from '../../../views/login-views/login-view/login-view-location-state';
import { recordForcedRelogin } from './force-relogin-loop-guard';

/**
 * Force the user back to the login page after auth has been lost.
 *
 * PLT-4720: the access / refresh JWTs now live in HttpOnly cookies. The
 * authoritative server-side clear happens when the SPA next calls
 * `POST /api/logout` (or naturally on cookie expiry); the local cleanup
 * here just (a) purges any legacy `localStorage` JWT entries that have
 * not yet been migrated out, and (b) navigates the user to `/login` so
 * they re-authenticate.
 *
 * We deliberately don't await a `POST /api/logout` round-trip here — the
 * redirect must happen synchronously so the user is not left on an
 * authenticated-looking page after a 401. The server cookie either
 * (i) was already invalid (which is why we landed here), in which case
 * there is nothing to clear, or (ii) will be cleared on the next legit
 * logout via the user's explicit action.
 */
export function removeTokenFromStorageAndForceNewLogin() {
  redirectToLogin('session-expired');
}

/**
 * `platform` is `/login`. `dma-sign-up` is the sign-up tab of the freemium sign-up page, so a
 * visitor without an account sees the create-account form first and one with an account is a
 * "Log in" click away (PLT-8049). It takes only the `/go/…` URLs that `isDmaOnboardingNextUrl`
 * allows (PLT-7567).
 */
export type LoginPage = 'platform' | 'dma-sign-up';

/**
 * Send a visitor who has no session at all to a login page, keeping the path and
 * query string so they resume where they were headed.
 *
 * Distinct from `removeTokenFromStorageAndForceNewLogin` only in what the login
 * page says. Entry points that can be a visitor's *first* page load — a chat
 * deep link, a `/go/…` campaign resolver — cannot tell an expired session from
 * never having had one, and for them "never" is the common case: the link
 * arrives in outbound material read by people who are not signed in. Claiming
 * their session expired is then simply untrue, and it reads as an error the
 * reader caused. A plain login page does not.
 */
export function forceLoginForVisitorWithoutSession(loginPage: LoginPage = 'platform') {
  redirectToLogin(undefined, loginPage);
}

function redirectToLogin(reasonCode: 'session-expired' | undefined, loginPage: LoginPage = 'platform') {
  clearLegacyAuthTokensFromStorage();
  // Stamp the loop-guard marker so the access-details safety net can avoid
  // immediately re-triggering a relogin (PLT-5124).
  recordForcedRelogin();
  const nextUrl = `${window.location.pathname}${window.location.search}`;
  const loginUrl =
    loginPage === 'dma-sign-up'
      ? // The sign-up page reads `nextUrl` as it is, so unlike `/login` it is encoded only once.
        appendSearchParamsToUrlPath(DMA_ONBOARDING_PATHS.SIGN_UP, { [DMA_ONBOARDING_NEXT_URL_PARAM]: nextUrl })
      : appendSearchParamsToUrlPath('/login', {
          nextUrl: encodeURIComponent(nextUrl),
          ...(reasonCode === undefined ? {} : { reasonCode }),
        } as LoginViewLocationState);

  window.location.assign(loginUrl);
}
